import React, { createContext, useContext, useEffect, useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

interface LanguageContextType {
  language: string;
  direction: 'ltr' | 'rtl';
  changeLanguage: (lang: string) => void;
  isRTL: boolean;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

interface LanguageProviderProps {
  children: React.ReactNode;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  console.log('🔄 LanguageProvider rendering');
  const { i18n } = useTranslation();
  const [language, setLanguage] = useState(i18n.language || 'en');
  const [direction, setDirection] = useState<'ltr' | 'rtl'>('ltr');

  const rtlLanguages = ['ar', 'he', 'fa', 'ur'];

  useEffect(() => {
    const currentLang = i18n.language || 'en';
    setLanguage(currentLang);
    
    const isRTL = rtlLanguages.includes(currentLang);
    const newDirection = isRTL ? 'rtl' : 'ltr';
    setDirection(newDirection);

    // Update document direction and language
    document.documentElement.dir = newDirection;
    document.documentElement.lang = currentLang;
    
    // Add/remove RTL class to body
    if (isRTL) {
      document.body.classList.add('rtl');
      document.body.classList.remove('ltr');
    } else {
      document.body.classList.add('ltr');
      document.body.classList.remove('rtl');
    }
  }, [i18n.language]);

  const changeLanguage = useCallback(async (lang: string) => {
    try {
      await i18n.changeLanguage(lang);
      setLanguage(lang);

      const isRTL = rtlLanguages.includes(lang);
      const newDirection = isRTL ? 'rtl' : 'ltr';
      setDirection(newDirection);

      // Update document direction and language
      document.documentElement.dir = newDirection;
      document.documentElement.lang = lang;

      // Add/remove RTL class to body
      if (isRTL) {
        document.body.classList.add('rtl');
        document.body.classList.remove('ltr');
      } else {
        document.body.classList.add('ltr');
        document.body.classList.remove('rtl');
      }

      // Store language preference
      localStorage.setItem('language', lang);
    } catch (error) {
      console.error('Failed to change language:', error);
    }
  }, [i18n, rtlLanguages]);

  const value: LanguageContextType = useMemo(() => {
    console.log('🔄 LanguageContext value recreated', {
      language,
      direction,
      isRTL: direction === 'rtl'
    });
    return {
      language,
      direction,
      changeLanguage,
      isRTL: direction === 'rtl',
    };
  }, [language, direction, changeLanguage]);

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};
